import { useCallback, useRef, useState } from 'react';
import { listUsers } from '../api/adminUsersApi';
import { logError } from '../utils/errorHandling';
import type { components } from '../api/types.gen';

type UserResponse = components['schemas']['UserResponse'];

const DEFAULT_CHUNK_SIZE = 100;

/** id → user object cache, keyed by the user's id coerced to a string key. */
export type UserNamesMap = Record<string, UserResponse>;

/**
 * Tuple returned by {@link useUserNameResolver}:
 * `[names, resolveNames, mergeNames]`. `mergeNames` lets a caller fold in
 * user objects it already has from another API call (e.g. a search result)
 * without a redundant `listUsers({ ids })` round trip.
 */
export type UseUserNameResolverResult = [
  UserNamesMap,
  (ids: Array<number | string>) => Promise<void>,
  (users: UserResponse[]) => void,
];

/**
 * Resolves user ids to {id, name, email} via adminUsersApi.listUsers({ids}),
 * chunked and cached across calls (a ref cache so repeated calls with
 * already-known ids skip the network). Used to display denormalized
 * teacher/student names for ids-only relations (ClassSectionsPanel's
 * teacherId, StudentPassLimitsPanel's studentId).
 *
 * @param logContext - passed to logError on a per-chunk failure
 * @param chunkSize - number of ids per listUsers request
 * @returns [names, resolveNames, mergeNames]
 */
export function useUserNameResolver(
  logContext: string,
  chunkSize: number = DEFAULT_CHUNK_SIZE
): UseUserNameResolverResult {
  const [names, setNames] = useState<UserNamesMap>({});
  const namesRef = useRef<UserNamesMap>({});

  const mergeNames = useCallback((users: UserResponse[]): void => {
    const entries: UserNamesMap = {};
    users.forEach((u) => {
      entries[u.id] = u;
    });
    namesRef.current = { ...namesRef.current, ...entries };
    setNames((prev) => ({ ...prev, ...entries }));
  }, []);

  const resolveNames = useCallback(
    async (ids: Array<number | string>): Promise<void> => {
      const unresolved = [...new Set(ids)].filter(
        (id) => !namesRef.current[id]
      );
      if (unresolved.length === 0) return;

      const newEntries: UserNamesMap = {};
      for (let i = 0; i < unresolved.length; i += chunkSize) {
        const chunk = unresolved.slice(i, i + chunkSize);
        try {
          const result = await listUsers({ ids: chunk, limit: chunk.length });
          result.data.forEach((u) => {
            newEntries[u.id] = u;
          });
        } catch (error) {
          logError(error, logContext);
        }
      }
      if (Object.keys(newEntries).length > 0)
        mergeNames(Object.values(newEntries));
    },
    [logContext, chunkSize, mergeNames]
  );

  return [names, resolveNames, mergeNames];
}

export default useUserNameResolver;
